/**
 * 文本节点：保存提示词 / 说明 / AI 文本结果。
 *
 * 纯内容卡片（无头部 chrome，对齐 infinite-canvas）：双击卡片进入行内富文本编辑，
 * Esc/Ctrl+Enter 或点击外部提交；工具栏「放大编辑」打开大编辑弹窗（长文本友好）。
 * 内容以 HTML 存储（历史纯文本兼容），渲染前白名单净化；下游消费自动转纯文本
 * （见 upstream.aggregateUpstreamText / utils/richTextBox）。
 * 工具栏另有：字号调节 + 「生图」「生视频」快捷创建下游配置节点并连线。
 */
import { useLayoutEffect, useRef, useState } from 'react';
import { Image as ImageIcon, Video, Minus, Plus, Maximize2 } from 'lucide-react';
import type { CanvasNode, TextNodeData } from '../types';
import { NodeWrapper } from './NodeWrapper';
import { RichTextEditor } from '../components/RichTextEditor';
import { looksLikeHtml, sanitizeRichTextHtml } from '../utils/richTextBox';
import { NodeToolbarButton, NodeToolbarDivider } from './NodeToolbar';
import { boardActions, getBoardState } from '../state/boardStore';
import { makeNode, nid } from '../utils';
import { defaultNodeData } from '../NodeCreateMenu';
import { DEFAULT_NODE_SIZES } from '../constants';
import { TextEditorDialog } from '../components/dialogs/TextEditorDialog';
import toast from '../../../components/Toast';

interface TextNodeProps {
  node: CanvasNode<TextNodeData>;
  selected: boolean;
  highlighted: boolean;
}

const FONT_SIZE_STEPS = [12, 14, 16, 18, 20, 24, 28, 32];
const DEFAULT_FONT_SIZE = 14;

function getNextFontSize(current: number, direction: 'up' | 'down'): number {
  if (direction === 'up') {
    const next = FONT_SIZE_STEPS.find((s) => s > current);
    return next ?? FONT_SIZE_STEPS[FONT_SIZE_STEPS.length - 1];
  }
  const reversed = [...FONT_SIZE_STEPS].reverse();
  const next = reversed.find((s) => s < current);
  return next ?? FONT_SIZE_STEPS[0];
}

export function TextNode({ node, selected, highlighted }: TextNodeProps) {
  const [editing, setEditing] = useState(false);
  const [expanded, setExpanded] = useState(false);
  // 编辑过程中维护本地 draft（HTML），提交时净化后一次性写回 store
  const [draft, setDraft] = useState('');
  const editorHostRef = useRef<HTMLDivElement | null>(null);

  const content = node.data.content || '';

  // 字号自适应：以文本节点默认宽度为基准，卡片变宽时文字同比放大
  //（0.6~3 倍封顶，避免拖窄后文字过小 / 拖宽后字号失控）；
  // 工具栏手动调节的字号作为「基准值」，实际渲染 = 基准 × 该比例
  const baseFontSize = node.data.fontSize ?? DEFAULT_FONT_SIZE;
  const fontScale = Math.min(3, Math.max(0.6, node.width / DEFAULT_NODE_SIZES.text.width));
  const effectiveFontSize = baseFontSize * fontScale;

  useLayoutEffect(() => {
    if (editing) {
      setDraft(sanitizeRichTextHtml(content));
      requestAnimationFrame(() => {
        const editor = editorHostRef.current?.querySelector('[contenteditable]');
        (editor as HTMLElement | null)?.focus();
      });
    }
    // 仅在进入编辑时初始化 draft；外部变化不需要在编辑中同步
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing]);

  const commit = () => {
    setEditing(false);
    const clean = sanitizeRichTextHtml(draft);
    // 只做 HTML 层对比：sanitize 幂等，能同时覆盖文本与格式变化。
    // 旧写法 && 纯文本比较会把"文本相同、仅格式变化"的编辑误判为无变化而静默丢弃
    if (clean !== sanitizeRichTextHtml(content)) {
      boardActions.updateNodeData(node.id, { content: clean });
      boardActions.commitNodeMutation();
    }
  };

  const createGenerateNode = (kind: 'image' | 'video') => {
    // 防重复：已存在从本节点连出的同类配置卡时不再新建，避免连点创建一堆重复卡
    const nodeType = kind === 'image' ? 'generate_image' : 'generate_video';
    const { nodes, edges } = getBoardState();
    const exists = edges.some((e) => {
      if (e.sourceNodeId !== node.id) return false;
      return nodes.find((n) => n.id === e.targetNodeId)?.type === nodeType;
    });
    if (exists) {
      toast(kind === 'image' ? '已存在关联的生图配置卡，请直接使用' : '已存在关联的生视频配置卡，请直接使用');
      return;
    }
    const x = node.x + node.width + 60;
    const y = node.y;
    // 复用 defaultNodeData：与工具栏 / 双击 / 右键 / 拖线创建路径一致，
    // 自动叠加「上次使用参数记忆」（模型 / 比例 / 清晰度 / 配音开关等）
    const newNode = makeNode(nodeType, { x, y }, defaultNodeData(nodeType), undefined, node.zIndex);
    boardActions.addNode(newNode);
    boardActions.addEdge({
      id: nid(),
      sourceNodeId: node.id,
      targetNodeId: newNode.id,
      sourceHandle: 'right',
      targetHandle: 'left',
    });
  };

  return (
    <NodeWrapper
      node={node}
      selected={selected}
      highlighted={highlighted}
      stretchContent
      toolbar={
        <>
            <NodeToolbarButton onClick={() => setExpanded(true)} title="放大编辑（富文本，适合长文本）">
              <Maximize2 className="h-3.5 w-3.5" />
            </NodeToolbarButton>
            <NodeToolbarDivider />
            <NodeToolbarButton
              onClick={() => {
                const cur = node.data.fontSize ?? DEFAULT_FONT_SIZE;
                boardActions.updateNodeData(node.id, { fontSize: getNextFontSize(cur, 'down') });
              }}
              title="缩小字体"
            >
              <Minus className="h-3.5 w-3.5" />
            </NodeToolbarButton>
            <span
              className="min-w-[1.5rem] text-center text-xs tabular-nums text-slate-500"
              title={`基准字号 ${baseFontSize}（实际显示约 ${Math.round(effectiveFontSize)}px，随卡片宽度缩放）`}
            >
              {node.data.fontSize ?? DEFAULT_FONT_SIZE}
            </span>
            <NodeToolbarButton
              onClick={() => {
                const cur = node.data.fontSize ?? DEFAULT_FONT_SIZE;
                boardActions.updateNodeData(node.id, { fontSize: getNextFontSize(cur, 'up') });
              }}
              title="放大字体"
            >
              <Plus className="h-3.5 w-3.5" />
            </NodeToolbarButton>
            <NodeToolbarDivider />
            <NodeToolbarButton onClick={() => createGenerateNode('image')} tone="primary" title="基于本节点生图">
              <ImageIcon className="h-3 w-3" />
              生图
            </NodeToolbarButton>
            <NodeToolbarButton onClick={() => createGenerateNode('video')} tone="primary" title="基于本节点生视频">
              <Video className="h-3 w-3" />
              生视频
            </NodeToolbarButton>
        </>
      }
    >
      <div
        onDoubleClick={(e) => {
          e.stopPropagation();
          setEditing(true);
        }}
        className="flex min-h-[100px] flex-1 cursor-text flex-col"
        title="双击编辑"
        style={{ fontSize: effectiveFontSize }}
      >
        {editing ? (
          <div
            ref={editorHostRef}
            className="flex min-h-0 flex-1 flex-col"
            onBlur={(e) => {
              // 工具栏按钮已阻止焦点转移；焦点真正离开编辑器（点到外部）时提交
              if (!e.currentTarget.contains(e.relatedTarget as Node)) commit();
            }}
            onKeyDown={(e) => {
              if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                e.preventDefault();
                commit();
              }
              if (e.key === 'Escape') {
                e.preventDefault();
                setEditing(false);
              }
            }}
            onDoubleClick={(e) => e.stopPropagation()}
          >
            <RichTextEditor
              value={draft}
              onChange={setDraft}
              compact
              placeholder="输入文字内容（Ctrl+Enter 提交）"
              className="min-h-[100px] flex-1"
              style={{ fontSize: 'inherit' }}
            />
          </div>
        ) : looksLikeHtml(content) ? (
          // 富文本渲染：白名单净化后渲染（快照可导出导入，必须净化）
          <div
            className="rich-text-preview min-h-0 flex-1 overflow-y-auto break-words leading-relaxed text-light-text [&_h3]:mt-2 [&_h3]:mb-1 [&_h3]:text-base [&_h3]:font-semibold [&_li]:ml-4 [&_ol_li]:list-decimal [&_ul_li]:list-disc [&_blockquote]:border-l-2 [&_blockquote]:border-slate-300 [&_blockquote]:pl-2 [&_blockquote]:text-slate-500"
            dangerouslySetInnerHTML={{ __html: sanitizeRichTextHtml(content) }}
          />
        ) : (
          <div className="min-h-0 flex-1 overflow-y-auto whitespace-pre-wrap break-words leading-relaxed text-light-text">
            {content || <span className="text-light-muted">双击输入文字</span>}
          </div>
        )}
      </div>

      {/* 放大编辑弹窗 */}
      {expanded && (
        <TextEditorDialog
          title="放大编辑（富文本）"
          initialValue={content}
          onCommit={(html) => {
            boardActions.updateNodeData(node.id, { content: html });
            boardActions.commitNodeMutation();
            setExpanded(false);
          }}
          onClose={() => setExpanded(false)}
        />
      )}
    </NodeWrapper>
  );
}
